import { createAction } from '@reduxjs/toolkit';
import {
  areSessionAttributesValid,
  buildFilter,
  MEETS,
  parseFilter
} from '../../../components/filters/filter-utils';
import * as itemTypes from '../../../items/item-types';
import {
  getItem as getItemInternal,
  saveItem as saveItemInternal
} from '../../../items/item.actions';
import { keyFromItemType } from '../../../routing-utils';
import { DEFAULT_END_DATE_STRING } from '../../campaign-utils';
import { isItemNew } from '../../../utils';
import { isFeatureEnabled } from '../../../feature-flags/feature-flags';
import {
  buildTargeting,
  COGS_TYPE_DOLLAR,
  COGS_TYPE_PERCENTAGE,
  OPTIMIZATION_TYPE_EVENT_BASED_ROAS,
  resolveSourceIds
} from './ad-group-utils';

export const receiveAdGroup = createAction('receiveAdGroup');
export const setAdGroupIsBusy = createAction('setAdGroupIsBusy');
export const modifyAdGroup = createAction('modifyAdGroup');
export const closeAdGroup = createAction('closeAdGroup');
export const setAdGroupHasValidationError = createAction(
  'setAdGroupHasValidationError'
);

const adCreativeModel = (id, name, params) => {
  return {
    id,
    name,
    type: itemTypes.AD_GROUP,
    [keyFromItemType(itemTypes.CAMPAIGN)]: params.campaignid,
    capStats: {
      overallConversionCap: 0,
      monthlyConversionCap: 0,
      dailyConversionCap: 0,
      overallRevenueCap: 0,
      monthlyRevenueCap: 0,
      dailyRevenueCap: 0
    }
  };
};

export const getItem = getItemInternal(
  adCreativeModel,
  receiveAdGroup,
  setAdGroupIsBusy,
  item => {
    const verticalIds = [];
    const excludedVerticalIds = [];
    const profileAttributes = [];
    const excludedProfileAttributes = [];
    let profileAttributesApply = MEETS[1].value;
    let excludedProfileAttributesApply = MEETS[1].value;
    // parse verticals separately
    if (item.targeting?.verticals.include?.length > 0) {
      verticalIds.push(...item.targeting.verticals?.include);
    }
    if (item.targeting?.verticals.exclude?.length > 0) {
      excludedVerticalIds.push(...item.targeting.verticals?.exclude);
    }
    if (item.targeting?.profileAttributeConditions?.include?.length > 0) {
      profileAttributes.push(
        ...item.targeting.profileAttributeConditions?.include
      );
    }
    if (item.targeting?.profileAttributeConditions?.exclude?.length > 0) {
      excludedProfileAttributes.push(
        ...item.targeting.profileAttributeConditions?.exclude
      );
    }
    if (item.targeting?.profileAttributeConditions?.includeMatchMode) {
      profileAttributesApply =
        item.targeting.profileAttributeConditions.includeMatchMode;
    }
    if (item.targeting?.profileAttributeConditions?.excludeMatchMode) {
      excludedProfileAttributesApply =
        item.targeting.profileAttributeConditions.excludeMatchMode;
    }

    // item.targeting is reassigned below, so capture the server copy first.
    const serverTargeting = item.targeting;
    const parsedFilter = parseFilter(item.filter, true);

    item.targeting = {
      ...parsedFilter,
      ...resolveSourceIds(serverTargeting, parsedFilter),
      verticalIds,
      excludedVerticalIds,
      profileAttributes,
      excludedProfileAttributes,
      profileAttributesApply,
      excludedProfileAttributesApply
    };

    // Transform ROAS fields from backend API contract to UI format
    if (isFeatureEnabled('AdGroupRoasBiddingEnabled')) {
      // Convert ltvEventIds (array of GUIDs) to ltvEvents (UI objects with conversionEventId + value)
      if (Array.isArray(item.ltvEventIds)) {
        item.ltvEvents = item.ltvEventIds.map(id => ({
          conversionEventId: id,
          value: null
        }));
      } else {
        item.ltvEvents = [];
      }

      // Rename estimatedIncentiveRedemptionRate back to incentiveRedemptionRate for UI
      if (item.estimatedIncentiveRedemptionRate !== undefined) {
        item.incentiveRedemptionRate = item.estimatedIncentiveRedemptionRate;
      }

      // COGS is stored in one of two backend fields (cogsPercentValue /
      // cogsDollarValue). Derive the UI-only `cogsType` flag from whichever one
      // is populated so the radio reflects the persisted unit.
      if (
        item.cogsPercentValue !== null &&
        item.cogsPercentValue !== undefined
      ) {
        item.cogsType = COGS_TYPE_PERCENTAGE;
      } else if (
        item.cogsDollarValue !== null &&
        item.cogsDollarValue !== undefined
      ) {
        item.cogsType = COGS_TYPE_DOLLAR;
      } else {
        item.cogsType = null;
      }
    }

    // Snapshot the persisted Advertiser Goal Target so we can enforce that it
    // cannot be cleared once set, while still allowing pre-existing nulls.
    item._originalAdvertiserGoalTarget = item.advertiserGoalTarget ?? null;

    // Same for the Auto-CPA fields, which the aggregate refuses to clear once set.
    item._originalAutoCpaTarget = item.autoCpaTarget ?? null;
    item._originalAutoCpaLookbackDays = item.autoCpaLookbackDays ?? null;

    // Whether the server has Auto-CPA on. Fill now persists bids on its own, and the server
    // rejects $0 bids while it still thinks Auto-CPA is on, so an unsaved uncheck is not enough.
    item._originalUseAutoCpa = !!item.useAutoCpa;
  }
);

const isBlank = value =>
  value === null || value === undefined || `${value}`.trim() === '';

// The onBeforeSave transform for a live Ad Group save's PUT: renames/strips
// UI-only fields (the netsuite line item wrapper, the RPI Override
// checkbox, ROAS UI fields) and rebuilds filter/targeting from the UI's
// FilterBuilder shape into what the backend accepts. Exported so
// schedule-mode-form.jsx can run the exact same transform before
// calculating/posting a scheduled change - unlike a live save's PUT, which
// tolerates an extra property it doesn't recognize, the schedule endpoint
// rejects (or, in a mixed change, silently drops) one it doesn't.
export const transformAdGroupForSave = itemCopy => {
  if (itemCopy.rpiOverrideChecked === false) {
    itemCopy.rpiOverrideExpiry = DEFAULT_END_DATE_STRING;
  }
  delete itemCopy.rpiOverrideChecked;

  // UI-only snapshot used for "cannot clear once set" validation.
  delete itemCopy._originalAdvertiserGoalTarget;
  delete itemCopy._originalAutoCpaTarget;
  delete itemCopy._originalAutoCpaLookbackDays;
  delete itemCopy._originalUseAutoCpa;

  // The Auto-CPA fields are Optional<T> server-side, where an explicit JSON null means "clear
  // this" - and the aggregate refuses to clear either one ("CPA Target cannot be cleared").
  // Every ad group created before Auto-CPA reads back from the read model as null, so sending
  // the GET response straight back would fail the save on an ad group nobody has configured.
  // Omitting the key sends Optional.Undefined instead, which means "untouched".
  ['autoCpaTarget', 'autoCpaLookbackDays'].forEach(field => {
    if (isBlank(itemCopy[field])) {
      delete itemCopy[field];
    }
  });

  // Read-model-only field; the payload model has no home for it.
  delete itemCopy.autoCpaLastEnabledDate;

  // A creative split only applies while ML Exploration is off, so it is dropped on save
  // rather than on toggle - an accidental toggle should not discard entered percentages.
  if (!itemCopy.forceMLCreativeTesting) {
    itemCopy.creativeSplits = [];
  }

  itemCopy.netsuiteAgencyLineId =
    itemCopy.netsuiteAdvertiserLineItem?.agencyLineId ?? null;
  delete itemCopy.netsuiteAdvertiserLineItem;

  // Transform ROAS fields to match backend API contract
  if (isFeatureEnabled('AdGroupRoasBiddingEnabled')) {
    // Convert ltvEvents (UI objects with conversionEventId + value) to ltvEventIds (array of GUIDs)
    if (Array.isArray(itemCopy.ltvEvents)) {
      itemCopy.ltvEventIds = itemCopy.ltvEvents
        .map(e => e.conversionEventId)
        .filter(Boolean);
    }
    delete itemCopy.ltvEvents;

    // Rename incentiveRedemptionRate to estimatedIncentiveRedemptionRate
    if (itemCopy.incentiveRedemptionRate !== undefined) {
      itemCopy.estimatedIncentiveRedemptionRate =
        itemCopy.incentiveRedemptionRate;
      delete itemCopy.incentiveRedemptionRate;
    }

    // COGS: persist only the selected unit's value and strip the UI-only
    // `cogsType` flag so the two backend fields are never both populated.
    if (itemCopy.cogsType === COGS_TYPE_PERCENTAGE) {
      itemCopy.cogsDollarValue = null;
    } else if (itemCopy.cogsType === COGS_TYPE_DOLLAR) {
      itemCopy.cogsPercentValue = null;
    } else {
      itemCopy.cogsPercentValue = null;
      itemCopy.cogsDollarValue = null;
    }
    delete itemCopy.cogsType;
  }

  //build filter from sourceid, audiences, conditions, meets, locations
  if (itemCopy.duplicate && !itemCopy.targeting) {
    return;
  } else {
    itemCopy.filter = buildFilter(itemCopy.targeting);
    const newTargeting = buildTargeting(itemCopy.targeting);
    itemCopy.targeting = newTargeting;
  }
  delete itemCopy.duplicate;
};

export const saveItem = saveItemInternal(
  modifyAdGroup,
  item => {
    if (!item.name) {
      return 'Name is a required field.';
    }
    if (!item.tuneOfferId) {
      return 'Tune Offer Id is a required field.';
    }
    if (!item.offerUrl) {
      return 'Offer Url is a required field.';
    }
    if (!item.assignedPlacements || item.assignedPlacements.length === 0) {
      return 'At least one placement is required, please add a placement.';
    }
    if (
      item.useDynamicCpc &&
      (!item.dynamicCpcConversionEventId || !item.dynamicCpcTargetPayout)
    ) {
      return 'Conversion Event and Target payout are required when Dynamic CPC is enabled.';
    }
    if (
      item.rpiOverrideChecked &&
      item.rpiOverrideExpiry === DEFAULT_END_DATE_STRING
    ) {
      return 'RPI Override requires an Expiry date.';
    }

    // A blank Auto-CPA field is omitted from the payload, so the server keeps its old value.
    // Once a value is on file, refuse the save rather than report success on a clear that
    // never happened.
    if (item._originalAutoCpaTarget != null && isBlank(item.autoCpaTarget)) {
      return 'Adgroup CPA target cannot be cleared once set.';
    }
    if (
      item._originalAutoCpaLookbackDays != null &&
      isBlank(item.autoCpaLookbackDays)
    ) {
      return 'Lookback window cannot be cleared once set.';
    }
    if (
      !isBlank(item.autoCpaLookbackDays) &&
      !Number.isInteger(Number(item.autoCpaLookbackDays))
    ) {
      return 'Lookback window must be a whole number of days.';
    }

    // --- Optimization / ROAS fields (only when the feature is enabled) ---
    if (isFeatureEnabled('AdGroupRoasBiddingEnabled')) {
      const isNew = isItemNew(item.id);

      // Optimization Type is required on new ad groups. Existing ad groups with
      // a pre-existing null are left untouched; once set it cannot be cleared
      // (the UI offers no empty option).
      if (isNew && !item.optimizationType) {
        return 'Optimization Type is a required field.';
      }

      // When an Optimization Type is set, the ROAS configuration is required.
      if (item.optimizationType) {
        const targetRoasFloorSet =
          item.targetRoasFloor !== null &&
          item.targetRoasFloor !== undefined &&
          `${item.targetRoasFloor}` !== '';
        if (!targetRoasFloorSet) {
          return 'Target ROAS Floor is required when an Optimization Type is set.';
        }
        if (!item.attributionWindow) {
          return 'Attribution Window is required when an Optimization Type is set.';
        }
        if (!item.spendScope) {
          return 'Spend Scope is required when an Optimization Type is set.';
        }
      }

      // Event-Based ROAS requires at least one fully configured Value Event.
      if (item.optimizationType === OPTIMIZATION_TYPE_EVENT_BASED_ROAS) {
        const ltvEvents = Array.isArray(item.ltvEvents) ? item.ltvEvents : [];
        if (!ltvEvents.some(e => e.conversionEventId)) {
          return 'At least one Value Event is required for Event-Based ROAS.';
        }
      }

      // Advertiser Goal Target is required on new ad groups, and on existing ad
      // groups it cannot be cleared once it has been set.
      const advertiserGoalTargetSet =
        item.advertiserGoalTarget !== null &&
        item.advertiserGoalTarget !== undefined &&
        `${item.advertiserGoalTarget}` !== '';
      if (
        (isNew || item._originalAdvertiserGoalTarget != null) &&
        !advertiserGoalTargetSet
      ) {
        return 'Advertiser Goal Target is a required field.';
      }

      // When a COGS Type is selected, its value is required.
      if (item.cogsType) {
        const cogsValue =
          item.cogsType === COGS_TYPE_PERCENTAGE
            ? item.cogsPercentValue
            : item.cogsDollarValue;
        const cogsValueSet =
          cogsValue !== null &&
          cogsValue !== undefined &&
          `${cogsValue}` !== '';
        if (!cogsValueSet) {
          return 'COGS Value is required when a COGS Type is selected.';
        }
      }
    }
    if (
      !item.duplicate &&
      (!item.targeting || !item.targeting.deviceTypes?.length > 0)
    ) {
      return 'Device type is required, please select a device type.';
    }
    if (
      !item.duplicate &&
      (!areSessionAttributesValid(item.targeting.sessionAttributes) ||
        !areSessionAttributesValid(item.targeting.excludedSessionAttributes))
    ) {
      return 'You have session attributes that are incomplete. Complete them or remove them before saving.';
    }
  },
  transformAdGroupForSave,
  (itemCopy, saved, dispatch, modifyItemAction) => {
    // Saving does not re-fetch the ad group, so move the "cannot clear once set" snapshots to
    // whatever this save persisted. A field left out of the payload kept its old value.
    const autoCpaSnapshots = {};
    if ('autoCpaTarget' in itemCopy)
      autoCpaSnapshots._originalAutoCpaTarget = itemCopy.autoCpaTarget;
    if ('autoCpaLookbackDays' in itemCopy)
      autoCpaSnapshots._originalAutoCpaLookbackDays =
        itemCopy.autoCpaLookbackDays;
    if ('useAutoCpa' in itemCopy)
      autoCpaSnapshots._originalUseAutoCpa = !!itemCopy.useAutoCpa;
    if (Object.keys(autoCpaSnapshots).length > 0) {
      dispatch((innerDispatch, getState) => {
        if (!getState()?.[itemTypes.AD_GROUP]?.byId?.[saved.id]) return;
        innerDispatch(
          modifyItemAction({
            id: saved.id,
            newProperties: { id: saved.id, ...autoCpaSnapshots }
          })
        );
      });
    }

    if (itemCopy.forceMLCreativeTesting) return;

    // Saving does not re-fetch the ad group, so mirror the creative split normalisation that
    // onBeforeSave applied to the payload. Without this the cleared split lingers in memory
    // and reappears in the Creative Split modal until the page is reloaded.
    dispatch((innerDispatch, getState) => {
      // The form stays editable while the request is in flight, so re-read state rather than
      // trusting the payload: Ops may have re-enabled manual splitting and typed new
      // percentages that must not be wiped, or navigated away from the ad group entirely.
      const current = getState()?.[itemTypes.AD_GROUP]?.byId?.[saved.id];
      if (!current || current.forceMLCreativeTesting) return;

      // Passing `id` inside newProperties takes the reducer's post-save branch, which leaves
      // isDirty false rather than marking the freshly saved form dirty again.
      innerDispatch(
        modifyItemAction({
          id: saved.id,
          newProperties: { id: saved.id, creativeSplits: [] }
        })
      );
    });
  }
);
